import { Link } from "react-router-dom";
import PageBanner from "../../PageBanner";
import SocialMediaSidebar from "../../SocialMediaSidebar";
import SectionHeader from "../../SectionHeader";
import smartBinImage from "../../../assets/images-webp/blog1.webp"; // Add your image path
import ScrollToTopButton from "../../ScrollToTopButton";
import SectionHeaderNoPDF from "../../SectionHeaderNoPDF";

const SmartBins = () => {
  return (
    <>
      <div>
        <SocialMediaSidebar />
      </div>
      <PageBanner
        title="How IoT is Revolutionizing Waste"
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "Resources" },
          { label: "Blogs" },
        ]}
      />
      <div className="max-w-6xl mx-auto p-4">
        <SectionHeaderNoPDF
          title="How IoT is Revolutionizing Waste @ AJEEVI"
          shareUrl="https://swmecom.ajeevi.in/blog/how-iot-is-revolutionizing-waste-collection"
        />
      </div>
      <div>
        <ScrollToTopButton />
      </div>

      {/* Page content */}
      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-28 py-10 bg-white text-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8 mb-10">
            <div className="lg:w-2/3">
              <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                Summary
              </h4>
              <p className="text-black mb-4 text-lg">
                The rapid urbanisation and industrial growth have resulted in
                massive garbage output, providing significant issues for effective
                waste management. Traditional approaches frequently fall short,
                resulting in environmental harm and increased operational costs.
              </p>
              <p className="text-black text-lg">
                The IoT-based Smart Waste Management System tackles these
                difficulties by effectively monitoring and managing waste through
                a network of sensors, data analytics, and automated technology,
                leading to more efficient collection, dynamic routing, and lower fuel use.
              </p>
            </div>
            <div className="lg:w-1/3">
              
              <p className="text-sm text-gray-500 mt-2 italic">
                IoT-enabled smart bins providing real-time fill level data
              </p>
            </div>
          </div>

          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              Introduction
            </h4>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <p className="text-black mb-4 text-lg">
                  In recent years, expanding urbanisation and industrial growth have
                  resulted in a large increase in waste creation, creating a
                  significant challenge to effective waste management. Traditional
                  waste management methods frequently fail in terms of efficiency
                  and sustainability.
                </p>
                <p className="text-black text-lg">
                  IoT-based systems provide real-time data on waste levels in bins,
                  enabling prompt collection and helping recycling efforts by
                  accurately separating different waste types at the source.
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#00D084]">
                <h5 className="font-bold text-lg mb-2">Did You Know?</h5>
                <p className="text-gray-700">
                  Cities using smart waste systems report 30-50% reductions in collection costs
                  and 60% fewer overflowing bins.
                </p>
              </div>
            </div>
          </div>

          <div className="mb-10">
            <h5 className="text-xl font-semibold mb-4 text-black">
              Key Processes in IoT-based Waste Management:
            </h5>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-blue-500">
                  <h6 className="font-semibold text-lg mb-2">Asset Tagging and Registration</h6>
                  <p className="text-gray-700">
                    RFID tags and QR codes uniquely identify each waste bin, storing location, 
                    bin size, and schedule data.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-green-500">
                  <h6 className="font-semibold text-lg mb-2">Sensor Deployment</h6>
                  <p className="text-gray-700">
                    Sensors integrated with collection vehicles track fill levels, 
                    bin status, and location in real time.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-purple-500">
                  <h6 className="font-semibold text-lg mb-2">Centralized Monitoring</h6>
                  <p className="text-gray-700">
                    Real-time dashboards allow tracking of bins, routes, and 
                    performance metrics from a central location.
                  </p>
                </div>
              </div>
              <div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-red-500">
                  <h6 className="font-semibold text-lg mb-2">Route Optimization</h6>
                  <p className="text-gray-700">
                    Smart routing algorithms reduce fuel consumption by up to 40% 
                    while improving collection efficiency.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-yellow-500">
                  <h6 className="font-semibold text-lg mb-2">Automated Alerts</h6>
                  <p className="text-gray-700">
                    The system triggers notifications when bins are full or when 
                    irregularities are detected in the collection process.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-indigo-500">
                  <h6 className="font-semibold text-lg mb-2">Integration with Facilities</h6>
                  <p className="text-gray-700">
                    Seamless connection with sorting facilities improves recycling 
                    rates by ensuring correct segregation at source.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-10 bg-gray-50 p-6 rounded-lg">
            <h5 className="text-xl font-semibold mb-4 text-black text-center">
              IoT Waste Technology Impact
            </h5>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl font-bold text-[#00D084] mb-2">40%</div>
                <p className="text-gray-700">Reduction in collection costs</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl font-bold text-[#00D084] mb-2">60%</div>
                <p className="text-gray-700">Fewer overflowing bins</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl font-bold text-[#00D084] mb-2">30%</div>
                <p className="text-gray-700">Increase in recycling rates</p>
              </div>
            </div>
          </div>

          <div className="mb-10">
            <h5 className="text-xl font-semibold mb-3 text-black">Conclusion</h5>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="md:w-2/3">
                <p className="text-black text-lg">
                  The widespread implementation of smart waste management systems
                  represents a transformative potential to solve the inefficiencies
                  and environmental consequences of traditional waste management
                  procedures. By leveraging IoT, AI, and data analytics, these
                  systems enable real-time monitoring, predictive maintenance, and
                  smarter recycling—contributing to a circular economy.
                </p>
              </div>
              <div className="md:w-1/3 bg-[#00D084] bg-opacity-10 p-4 rounded-lg">
                <h6 className="font-semibold text-lg mb-2">Future Outlook</h6>
                <p className="text-gray-700">
                  Next-gen systems will incorporate AI vision for waste identification
                  and blockchain for traceability in recycling chains.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-6 rounded-lg">
            <h5 className="text-xl font-semibold mb-3 text-black">
              Ajeevi's Contribution
            </h5>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-black mb-4 text-lg">
                  Ajeevi Technologies provides innovative IoT solutions for smart cities
                  and sustainable development, including comprehensive smart waste
                  management systems that transform urban sanitation.
                </p>
                <p className="text-black text-lg">
                  Learn more at{" "}
                  <a
                    href="http://www.ajeevi.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00D084] hover:underline"
                  >
                    www.ajeevi.com
                  </a>.
                </p>
              </div>
              <div>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black">
                  <li>
                    <strong>End-to-End IoT Solutions:</strong> From smart bins to fleet tracking
                  </li>
                  <li>
                    <strong>Advanced Analytics:</strong> Predictive collection and route optimization
                  </li>
                  <li>
                    <strong>Citizen Engagement:</strong> Mobile apps for reporting and feedback
                  </li>
                  <li>
                    <strong>Sustainability Integration:</strong> Connecting waste systems with circular economy
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SmartBins;